/** Promotions CRUD + rule editor (Phase D1).
 *
 * Promotion = group of rules. Каждое rule = discount + predicate tree.
 * Predicate tree редактируется как JSON textarea с подсказкой (для MVP).
 * Tree-builder UI — отдельная задача.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  BarChart3,
  Megaphone,
  Plus,
  Trash2,
} from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { PredicateBuilder } from "@/components/promotions/PredicateBuilder";
import { useDeleteConfirm } from "@/hooks/useDeleteConfirm";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";

interface Rule {
  id: number;
  discount_type: "VALUE_FIXED" | "VALUE_PERCENTAGE" | "SHIPPING";
  discount_value: string;
  conditions: Record<string, unknown>;
  sort_order: number;
  created_at: string;
}

interface Promotion {
  id: number;
  name: string;
  description: string | null;
  promotion_type: "CATALOGUE" | "ORDER";
  starts_at: string | null;
  ends_at: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  rules: Rule[];
}

const DISCOUNT_TYPES = ["VALUE_FIXED", "VALUE_PERCENTAGE", "SHIPPING"] as const;

function fmtSum(tiyin: number) {
  return `${Math.round(tiyin / 100).toLocaleString("ru-RU")} сум`;
}

export function PromotionsPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [editingId, setEditingId] = useState<number | "new" | null>(null);
  const [expandedRulesFor, setExpandedRulesFor] = useState<number | null>(null);

  const { data: promos, isLoading } = useQuery({
    queryKey: ["admin-promotions"],
    queryFn: async () => {
      const { data } = await api.get("/promotions");
      return data as Promotion[];
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/promotions/${id}`);
    },
    onSuccess: () => {
      toast.success("Промоакция удалена");
      queryClient.invalidateQueries({ queryKey: ["admin-promotions"] });
    },
  });

  const { askDelete, dialog: deleteDialog } = useDeleteConfirm<number>({
    onConfirm: (id) => deleteMutation.mutateAsync(id),
    title: "Удалить промоакцию?",
  });

  if (isLoading) return <Skeleton className="h-96" />;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Button asChild variant="ghost" size="sm">
          <Link to="/configuration">
            <ArrowLeft className="w-4 h-4 mr-1" />
            Конфигурация
          </Link>
        </Button>
      </div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading flex items-center gap-2">
            <Megaphone className="w-6 h-6" />
            Promotions (auto-discounts)
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Автоматические скидки с predicate tree. Применяются ко всем заказам.
          </p>
        </div>
        <Button onClick={() => setEditingId("new")}>
          <Plus className="w-4 h-4 mr-1" />
          Создать
        </Button>
      </div>

      {editingId === "new" && (
        <PromotionForm mode="create" onClose={() => setEditingId(null)} />
      )}

      <StatsSection />

      <div className="grid grid-cols-1 gap-3">
        {promos?.length === 0 && editingId !== "new" && (
          <Card>
            <CardContent className="py-10 text-center text-sm text-muted-foreground">
              Промоакций нет. Создайте первую.
            </CardContent>
          </Card>
        )}
        {promos?.map((p) => (
          <Card key={p.id}>
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <CardTitle className="text-base flex items-center gap-2">
                    {p.name}
                    {p.is_active ? (
                      <Badge variant="secondary" className="text-[10px]">
                        active
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-[10px]">
                        disabled
                      </Badge>
                    )}
                    <Badge variant="outline" className="text-[10px]">
                      {p.promotion_type}
                    </Badge>
                  </CardTitle>
                  {p.description && (
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {p.description}
                    </p>
                  )}
                </div>
                <Badge variant="outline">{p.rules.length} правил</Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {editingId === p.id ? (
                <PromotionForm
                  mode="edit"
                  promotion={p}
                  onClose={() => setEditingId(null)}
                />
              ) : (
                <div className="flex items-center justify-between">
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEditingId(p.id)}
                    >
                      Редактировать
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setExpandedRulesFor(
                          expandedRulesFor === p.id ? null : p.id,
                        )
                      }
                    >
                      Правила ({p.rules.length})
                    </Button>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => askDelete(p.id, p.name)}
                  >
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </div>
              )}
              {expandedRulesFor === p.id && <RulesEditor promotion={p} />}
            </CardContent>
          </Card>
        ))}
      </div>
      {deleteDialog}
    </div>
  );
}

function PromotionForm({
  mode,
  promotion,
  onClose,
}: {
  mode: "create" | "edit";
  promotion?: Promotion;
  onClose: () => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const [name, setName] = useState(promotion?.name ?? "");
  const [description, setDescription] = useState(promotion?.description ?? "");
  const [type, setType] = useState<Promotion["promotion_type"]>(
    promotion?.promotion_type ?? "ORDER",
  );
  const [startsAt, setStartsAt] = useState(promotion?.starts_at?.slice(0, 16) ?? "");
  const [endsAt, setEndsAt] = useState(promotion?.ends_at?.slice(0, 16) ?? "");
  const [isActive, setIsActive] = useState(promotion?.is_active ?? true);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        name: name.trim(),
        description: description.trim() || null,
        promotion_type: type,
        starts_at: startsAt ? new Date(startsAt).toISOString() : null,
        ends_at: endsAt ? new Date(endsAt).toISOString() : null,
        is_active: isActive,
      };
      if (mode === "create") {
        await api.post("/promotions", payload);
      } else if (promotion) {
        await api.patch(`/promotions/${promotion.id}`, payload);
      }
    },
    onSuccess: () => {
      toast.success(mode === "create" ? "Создано" : "Сохранено");
      queryClient.invalidateQueries({ queryKey: ["admin-promotions"] });
      onClose();
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.detail ?? "Ошибка"),
  });

  return (
    <Card className={mode === "edit" ? "border-primary/30" : undefined}>
      {mode === "create" && (
        <CardHeader>
          <CardTitle className="text-base">Новая промоакция</CardTitle>
        </CardHeader>
      )}
      <CardContent className="space-y-3 pt-4">
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>Название *</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Скидка 10% на акционные категории"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Тип *</Label>
            <Select
              value={type}
              onValueChange={(v: Promotion["promotion_type"]) => setType(v)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ORDER">ORDER (на корзину)</SelectItem>
                <SelectItem value="CATALOGUE">CATALOGUE (per item)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="space-y-1.5">
          <Label>Описание</Label>
          <Textarea
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>Начало</Label>
            <Input
              type="datetime-local"
              value={startsAt}
              onChange={(e) => setStartsAt(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Окончание</Label>
            <Input
              type="datetime-local"
              value={endsAt}
              onChange={(e) => setEndsAt(e.target.value)}
            />
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <Checkbox
            checked={isActive}
            onChange={(e) =>
              setIsActive((e.target as HTMLInputElement).checked)
            }
          />
          Активна
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button
            onClick={() => saveMutation.mutate()}
            disabled={!name.trim() || saveMutation.isPending}
          >
            {mode === "create" ? "Создать" : "Сохранить"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function RulesEditor({ promotion }: { promotion: Promotion }): JSX.Element {
  const queryClient = useQueryClient();
  const [addingRule, setAddingRule] = useState(false);

  const deleteMutation = useMutation({
    mutationFn: async (ruleId: number) => {
      await api.delete(`/promotions/rules/${ruleId}`);
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["admin-promotions"] }),
  });

  return (
    <div className="border-t pt-3 space-y-2">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium">Правила</h4>
        <Button size="sm" onClick={() => setAddingRule(true)}>
          <Plus className="w-3 h-3 mr-1" />
          Добавить правило
        </Button>
      </div>

      {addingRule && (
        <RuleForm
          promotionId={promotion.id}
          mode="create"
          onClose={() => setAddingRule(false)}
        />
      )}

      {promotion.rules.length === 0 ? (
        <p className="text-xs text-muted-foreground text-center py-4">
          Правил нет. Добавьте первое.
        </p>
      ) : (
        <div className="space-y-2">
          {promotion.rules.map((r) => (
            <RuleRow
              key={r.id}
              rule={r}
              onDelete={() => deleteMutation.mutate(r.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function RuleRow({
  rule,
  onDelete,
}: {
  rule: Rule;
  onDelete: () => void;
}): JSX.Element {
  const [editMode, setEditMode] = useState(false);
  const { askDelete, dialog: deleteDialog } = useDeleteConfirm<true>({
    onConfirm: () => onDelete(),
    title: "Удалить правило?",
  });

  if (editMode) {
    return (
      <RuleForm
        mode="edit"
        rule={rule}
        promotionId={null}
        onClose={() => setEditMode(false)}
      />
    );
  }

  const summary = describeRule(rule);
  return (
    <div className="border rounded p-2 bg-muted/10 flex items-center gap-2">
      <Badge variant="outline" className="text-[10px]">
        {rule.discount_type}
      </Badge>
      <span className="text-xs flex-1 truncate">{summary}</span>
      <Button size="sm" variant="ghost" onClick={() => setEditMode(true)}>
        Изм.
      </Button>
      <Button
        size="icon"
        variant="ghost"
        onClick={() => askDelete(true)}
      >
        <Trash2 className="w-3.5 h-3.5 text-destructive" />
      </Button>
      {deleteDialog}
    </div>
  );
}

function RuleForm({
  promotionId,
  mode,
  rule,
  onClose,
}: {
  promotionId: number | null;
  mode: "create" | "edit";
  rule?: Rule;
  onClose: () => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const [discountType, setDiscountType] = useState<Rule["discount_type"]>(
    rule?.discount_type ?? "VALUE_PERCENTAGE",
  );
  const [discountValue, setDiscountValue] = useState(
    rule?.discount_value ?? "10",
  );
  const [sortOrder, setSortOrder] = useState(rule?.sort_order ?? 0);
  const [conditions, setConditions] = useState<Record<string, unknown>>(
    rule?.conditions ?? {},
  );

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        discount_type: discountType,
        discount_value: discountValue,
        conditions,
        sort_order: sortOrder,
      };
      if (mode === "create" && promotionId != null) {
        await api.post(`/promotions/${promotionId}/rules`, payload);
      } else if (rule) {
        await api.patch(`/promotions/rules/${rule.id}`, payload);
      }
    },
    onSuccess: () => {
      toast.success("Сохранено");
      queryClient.invalidateQueries({ queryKey: ["admin-promotions"] });
      onClose();
    },
    onError: (e: any) =>
      toast.error(e?.message || e?.response?.data?.detail || "Ошибка"),
  });

  return (
    <div className="border rounded p-3 bg-background space-y-2">
      <div className="grid grid-cols-3 gap-2">
        <div className="space-y-1">
          <Label className="text-xs">Тип скидки</Label>
          <Select
            value={discountType}
            onValueChange={(v: Rule["discount_type"]) => setDiscountType(v)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DISCOUNT_TYPES.map((t) => (
                <SelectItem key={t} value={t}>
                  {t}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">
            Значение{" "}
            {discountType === "VALUE_PERCENTAGE"
              ? "(%)"
              : discountType === "VALUE_FIXED"
                ? "(сумы)"
                : "(не используется)"}
          </Label>
          <Input
            type="number"
            min={0}
            step="0.01"
            value={discountValue}
            onChange={(e) => setDiscountValue(e.target.value)}
            disabled={discountType === "SHIPPING"}
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Sort</Label>
          <Input
            type="number"
            value={sortOrder}
            onChange={(e) => setSortOrder(Number(e.target.value))}
          />
        </div>
      </div>
      <PredicateBuilder value={conditions} onChange={setConditions} />
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={onClose}>
          Отмена
        </Button>
        <Button size="sm" onClick={() => saveMutation.mutate()}>
          {mode === "create" ? "Добавить" : "Сохранить"}
        </Button>
      </div>
    </div>
  );
}

interface PromotionStats {
  promotion_id: number;
  name: string;
  usage_count: number;
  total_discount_tiyin: number;
  unique_clients: number;
  last_applied_at: string | null;
}

function StatsSection(): JSX.Element | null {
  const { data: stats } = useQuery({
    queryKey: ["promotion-stats"],
    queryFn: async () => {
      const { data } = await api.get("/promotions/stats");
      return data as PromotionStats[];
    },
  });

  const filtered = stats?.filter((s) => s.usage_count > 0) ?? [];
  if (filtered.length === 0) return null;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <BarChart3 className="w-4 h-4" />
          Аналитика срабатываний
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-1">
          {filtered.map((s) => (
            <div
              key={s.promotion_id}
              className="flex items-center justify-between gap-3 py-1.5 border-b last:border-0 text-sm"
            >
              <span className="font-medium truncate flex-1">{s.name}</span>
              <span className="text-xs text-muted-foreground">
                {s.usage_count}× · {s.unique_clients} клиент(ов)
                {s.last_applied_at && (
                  <> · {new Date(s.last_applied_at).toLocaleDateString("ru-RU")}</>
                )}
              </span>
              <span className="font-semibold text-success tabular-nums w-32 text-right">
                −{fmtSum(s.total_discount_tiyin)}
              </span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function describeRule(rule: Rule): string {
  const parts: string[] = [];
  if (rule.discount_type === "VALUE_PERCENTAGE") {
    parts.push(`-${rule.discount_value}%`);
  } else if (rule.discount_type === "VALUE_FIXED") {
    parts.push(`-${fmtSum(Number(rule.discount_value) * 100)}`);
  } else if (rule.discount_type === "SHIPPING") {
    parts.push("бесплатная доставка");
  }
  const conds = rule.conditions || {};
  if (Object.keys(conds).length === 0) {
    parts.push("без условий");
  } else {
    parts.push(JSON.stringify(conds).slice(0, 80));
  }
  return parts.join(" · ");
}
